// Version: 1.10
// v1.10: Phone width: the client risk table stacks one client per block with each value labelled (index.css v1.6), instead of scrolling sideways.
// v1.9: Buttons use the shared button system (CV-49): 8px corners, standard variants and sizes, one selected look; no inline radius, padding or font size.
// v1.8: Welcome back uses the person's first name when it's set on Users,
//       falling back to their email.
// v1.7: Tracking snippets to update: client sites still sending an older
//       analytics snippet (app.py v4.04), each linking to the snippet in the
//       client's Digital Hub. They also appear in Needs attention.
// v1.6: Sales 'Follow-ups due' now shows the next 7 days (not just due/overdue),
//        so tag-scheduled follow-ups appear ahead of time, each with its date.
// v1.5: Quick action renamed Reports and Assessments, describing all three outputs.
// v1.4: Clickable and filterable. Each headline number jumps to its section
//       and sets a matching filter (Need attention, Clients, Assessed with its
//       A–B, C–D and E–F pills, Open cyber issues, Expiring). Needs attention
//       filters by severity and area; Client risk has a search box and filters
//       for grade band, not assessed, has issues and getting worse; Expiring
//       filters by domain or certificate and can hide auto-renewing ones.
//       New Sales pipeline section (admin): open leads, open value, hot leads,
//       overdue follow-ups and win rate, a bar of open leads by stage, the
//       hottest leads and the follow-ups due, each linking to the lead.
//       Overdue sales follow-ups also appear in Needs attention.
// v1.3: Rebuilt as an admin overview from GET /api/admin/overview (app.py 3.79)
//       plus /api/tickets/stats. Sections: needs attention (worst first, each
//       linking to where it is fixed), platform health (admin only), expiring in
//       the next 30 days, client risk table (grade, open cyber hub issues and the
//       change since the last daily snapshot), and assessment coverage (never
//       assessed, out of date, grade dropped). Refresh rebuilds the cached
//       overview. Falls back to the client list if the overview can't load.
// v1.2: Product name in the page title.
import React, { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { authFetch, useAuth } from './auth';
import './Dashboard.css';

const OPEN_STAGES = ['New', 'Researching', 'Contacted', 'Engaged', 'Proposal'];
const STAGE_COLOURS = {
  New: '#8e8e93', Researching: '#64d2ff', Contacted: '#0a84ff', Engaged: '#bf5af2', Proposal: '#ff9f0a',
};
const GRADE_BANDS = [
  { id: 'AB', label: 'A–B', test: g => g === 'A' || g === 'B' },
  { id: 'CD', label: 'C–D', test: g => g === 'C' || g === 'D' },
  { id: 'EF', label: 'E–F', test: g => g === 'E' || g === 'F' },
  { id: 'none', label: 'Not assessed', test: g => !g },
];

function GradeBadge({ grade }) {
  const letter = grade ? String(grade)[0] : '?';
  const cls = ['A','B','C','D','E','F'].includes(letter) ? `grade-${letter}` : 'grade-unknown';
  return <span className={`grade-badge ${cls}`}>{grade || '?'}</span>;
}

function formatDate(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  if (isNaN(d)) return '—';
  return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
}

function formatTime(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  if (isNaN(d)) return '';
  return d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', timeZone: 'Europe/London' }) + ' GMT';
}

function money(v) {
  const n = Number(v || 0);
  return '£' + n.toLocaleString('en-GB', { maximumFractionDigits: 0 });
}

function plural(n, word) {
  return `${n} ${word}${n === 1 ? '' : 's'}`;
}

function todayYmd() {
  return new Date().toLocaleDateString('en-CA', { timeZone: 'Europe/London' });
}

function Dot({ rag }) {
  return <span className={`dash-dot dash-dot-${rag || 'amber'}`} />;
}

function Section({ title, link, linkLabel, count, children, innerRef, right }) {
  return (
    <div className="dash-section" ref={innerRef}>
      <div className="dash-section-header">
        <h2 className="dash-section-title">
          {title}
          {count != null && <span className="dash-count">{count}</span>}
        </h2>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          {right}
          {link && <Link to={link} className="dash-section-link">{linkLabel || 'View all'} →</Link>}
        </div>
      </div>
      {children}
    </div>
  );
}

function Chip({ active, onClick, children, tone }) {
  return (
    <button type="button" className={`dash-chip ${active ? 'active' : ''} ${tone ? `tone-${tone}` : ''}`} onClick={onClick}>
      {children}
    </button>
  );
}

function StatTile({ value, label, tone, active, onClick, children, title }) {
  return (
    <button type="button" className={`stat-card stat-click ${active ? 'active' : ''}`} onClick={onClick} title={title}>
      <div className={`stat-value ${tone || ''}`}>{value}</div>
      <div className="stat-label">{label}</div>
      {children}
    </button>
  );
}

// Ticket stats become attention items alongside the overview's own
function ticketAttention(t) {
  if (!t) return [];
  const out = [];
  if (t.sla_breached > 0) out.push({ severity: 'red', area: 'Tickets', title: `${plural(t.sla_breached, 'ticket')} past SLA`, detail: 'Not resolved within the agreed response time', link: '/tickets' });
  if (t.critical_open > 0) out.push({ severity: 'red', area: 'Tickets', title: `${plural(t.critical_open, 'critical ticket')} open`, detail: '', link: '/tickets' });
  if (t.unassigned_open > 0) out.push({ severity: 'amber', area: 'Tickets', title: `${plural(t.unassigned_open, 'open ticket')} not assigned`, detail: '', link: '/tickets' });
  return out;
}

function salesAttention(s) {
  if (!s || !s.overdue) return [];
  return [{ severity: 'amber', area: 'Sales', title: `${plural(s.overdue, 'sales follow-up')} overdue`,
            detail: s.due_today ? `${s.due_today} more due today` : 'Next action date has passed', link: '/sales/pipeline' }];
}

function areaGroup(a) {
  return a.area === 'Platform' || a.area === 'Tickets' || a.area === 'Sales' ? a.area : 'Clients';
}

function expiryText(days) {
  if (days < 0) return `Expired ${plural(Math.abs(days), 'day')} ago`;
  if (days === 0) return 'Expires today';
  return `${plural(days, 'day')}`;
}

export default function Dashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const isAdmin = user?.role === 'admin';
  const [productName, setProductName] = useState('');
  const [data,        setData]        = useState(null);
  const [tickets,     setTickets]     = useState(null);
  const [sales,       setSales]       = useState(null);     // { stats, leads }
  const [fallback,    setFallback]    = useState(null);
  const [loading,     setLoading]     = useState(true);
  const [refreshing,  setRefreshing]  = useState(false);
  const [showAllClients, setShowAllClients] = useState(false);

  // Filters
  const [activeTile, setActiveTile] = useState('');
  const [attnSeverity, setAttnSeverity] = useState('all');
  const [attnArea, setAttnArea] = useState('all');
  const [clientQuery, setClientQuery] = useState('');
  const [gradeBand, setGradeBand] = useState('all');
  const [issuesOnly, setIssuesOnly] = useState(false);
  const [risingOnly, setRisingOnly] = useState(false);
  const [expKind, setExpKind] = useState('all');
  const [hideAutoRenew, setHideAutoRenew] = useState(false);

  const refs = { attention: useRef(null), risk: useRef(null), expiring: useRef(null), coverage: useRef(null), sales: useRef(null) };
  const scrollTo = (k) => setTimeout(() => refs[k].current && refs[k].current.scrollIntoView({ behavior: 'smooth', block: 'start' }), 30);

  useEffect(() => {
    fetch('/api/health').then(r => r.json()).then(d => setProductName(d.product || '')).catch(() => {});
  }, []);

  const load = useCallback((refresh = false) => {
    if (refresh) setRefreshing(true);
    const getJson = (url) => authFetch(url).then(r => {
      if (r.status === 401) { logout(); navigate('/login'); return null; }
      return r.ok ? r.json() : null;
    }).catch(() => null);
    const jobs = [
      getJson(`/api/admin/overview${refresh ? '?refresh=1' : ''}`),
      getJson('/api/tickets/stats'),
      isAdmin ? getJson('/api/sales/stats') : Promise.resolve(null),
      isAdmin ? getJson('/api/sales/leads') : Promise.resolve(null),
    ];
    Promise.all(jobs).then(([o, t, ss, sl]) => {
      setTickets(t);
      setSales(ss ? { stats: ss, leads: Array.isArray(sl) ? sl : [] } : null);
      if (o && o.stats) {
        setData(o);
      } else {
        authFetch('/api/clients').then(r => (r.ok ? r.json() : [])).then(c => setFallback(Array.isArray(c) ? c : [])).catch(() => setFallback([]));
      }
      setLoading(false);
      setRefreshing(false);
    });
  }, [logout, navigate, isAdmin]);

  useEffect(() => { load(false); }, [load]);

  // ── Derived lists ─────────────────────────────────────────────────────────
  const attention = useMemo(() => [
    ...ticketAttention(tickets), ...salesAttention(sales?.stats), ...((data && data.attention) || []),
  ].sort((a, b) => (b.severity === 'red') - (a.severity === 'red')), [tickets, sales, data]);

  const attnShown = attention.filter(a =>
    (attnSeverity === 'all' || a.severity === attnSeverity) && (attnArea === 'all' || areaGroup(a) === attnArea));
  const attnAreas = ['Platform', 'Clients', 'Tickets', 'Sales'].filter(g => attention.some(a => areaGroup(a) === g));

  const riskClients = (data && data.clients) || [];
  const riskShown = riskClients.filter(c => {
    const g = c.grade ? String(c.grade)[0] : '';
    if (gradeBand !== 'all' && !GRADE_BANDS.find(b => b.id === gradeBand).test(g)) return false;
    if (issuesOnly && !c.issues) return false;
    if (risingOnly && !(c.delta > 0)) return false;
    if (clientQuery.trim() && !c.name.toLowerCase().includes(clientQuery.trim().toLowerCase())) return false;
    return true;
  });
  const riskFiltered = gradeBand !== 'all' || issuesOnly || risingOnly || clientQuery.trim();
  const riskVisible = showAllClients || riskFiltered ? riskShown : riskShown.slice(0, 10);

  const expiring = (data && data.expiring) || [];
  const snippets = (data && data.snippets) || null;   // v1.7
  const expShown = expiring.filter(e => (expKind === 'all' || e.kind === expKind) && !(hideAutoRenew && e.auto_renew));

  const salesView = useMemo(() => {
    if (!sales) return null;
    const open = sales.leads.filter(l => OPEN_STAGES.includes(l.stage));
    const today = todayYmd();
    const hot = [...open].filter(l => l.score).sort((a, b) => (b.score || 0) - (a.score || 0)).slice(0, 6);
    const horizon = (() => { const d = new Date(today + 'T00:00:00'); d.setDate(d.getDate() + 7); return d.toISOString().slice(0, 10); })();
    const due = open.filter(l => l.next_action_date && l.next_action_date <= horizon)
      .sort((a, b) => a.next_action_date.localeCompare(b.next_action_date)).slice(0, 8);
    const byStage = OPEN_STAGES.map(s => ({ stage: s, count: open.filter(l => l.stage === s).length }));
    return { open, hot, due, byStage, hotCount: open.filter(l => l.score_band === 'Hot').length, today };
  }, [sales]);

  function resetClientFilters() {
    setGradeBand('all'); setIssuesOnly(false); setRisingOnly(false); setClientQuery('');
  }
  function tileClick(id) {
    setActiveTile(prev => (prev === id ? '' : id));
    if (id === 'attention') { setAttnSeverity('all'); setAttnArea('all'); scrollTo('attention'); }
    if (id === 'clients') { resetClientFilters(); setShowAllClients(true); scrollTo('risk'); }
    if (id === 'assessed') { scrollTo('coverage'); }
    if (id === 'issues') { resetClientFilters(); setIssuesOnly(true); scrollTo('risk'); }
    if (id === 'expiring') { setExpKind('all'); setHideAutoRenew(false); scrollTo('expiring'); }
  }
  function gradePill(e, band) {
    e.stopPropagation();
    resetClientFilters(); setGradeBand(band); setActiveTile('assessed'); scrollTo('risk');
  }

  const header = (
    <div className="page-header dash-header">
      <div>
        <div className="page-title">{productName ? `${productName} Dashboard` : 'Dashboard'}</div>
        <div className="page-subtitle">Welcome back, {(user?.name || '').trim().split(/\s+/)[0] || user?.username}</div>
      </div>
      {data && (
        <div className="dash-refresh">
          <span>Updated {formatTime(data.generated_at)}</span>
          <button className="btn btn-secondary" onClick={() => load(true)} disabled={refreshing}>
            {refreshing ? 'Refreshing…' : 'Refresh'}
          </button>
        </div>
      )}
    </div>
  );

  if (loading) {
    return <div>{header}<div className="dash-empty"><span className="spinner" /></div></div>;
  }

  if (!data) {
    const clients = fallback || [];
    const graded = clients.filter(c => c.latest_report?.grade);
    return (
      <div>
        {header}
        <div className="dash-stats">
          <div className="stat-card"><div className="stat-value">{clients.length}</div><div className="stat-label">Total Clients</div></div>
          <div className="stat-card"><div className="stat-value">{graded.length}</div><div className="stat-label">Assessed</div></div>
        </div>
        <div className="dash-empty">The overview could not be loaded. Check the Portal API is on version 3.79 or later.</div>
      </div>
    );
  }

  const { stats, health, coverage } = data;
  const g = stats.grades || {};

  return (
    <div>
      {header}

      {/* ── Headline numbers ── */}
      <div className="dash-stats">
        <StatTile value={attention.length} label="Need attention" active={activeTile === 'attention'} onClick={() => tileClick('attention')}
          tone={attention.some(a => a.severity === 'red') ? 'red' : attention.length ? 'amber' : 'green'} />
        <StatTile value={stats.clients} label="Clients" active={activeTile === 'clients'} onClick={() => tileClick('clients')} title="Show every client in the risk table" />
        <StatTile value={stats.assessed} label="Assessed" active={activeTile === 'assessed'} onClick={() => tileClick('assessed')} title="Assessment coverage">
          <div className="stat-grades">
            {GRADE_BANDS.slice(0, 3).map(b => (
              <span key={b.id} role="button" tabIndex={0} className={`grade-pill grade-${b.id[0]} ${gradeBand === b.id ? 'active' : ''}`}
                onClick={e => gradePill(e, b.id)} onKeyDown={e => e.key === 'Enter' && gradePill(e, b.id)} title={`Show clients graded ${b.label}`}>
                {b.label} {b.id === 'AB' ? (g.A || 0) + (g.B || 0) : b.id === 'CD' ? (g.C || 0) + (g.D || 0) : (g.E || 0) + (g.F || 0)}
              </span>
            ))}
          </div>
        </StatTile>
        <StatTile value={stats.open_issues} label="Open cyber issues" active={activeTile === 'issues'} onClick={() => tileClick('issues')} title="Clients with open issues, most first" />
        <StatTile value={stats.expiring} label={`Expiring in ${data.expiry_days} days`} tone={stats.expiring ? 'amber' : ''}
          active={activeTile === 'expiring'} onClick={() => tileClick('expiring')} />
      </div>

      {/* ── Needs attention ── */}
      <Section title="Needs attention" count={attention.length || null} innerRef={refs.attention}>
        {attention.length > 0 && (
          <div className="dash-filters">
            <Chip active={attnSeverity === 'all'} onClick={() => setAttnSeverity('all')}>All</Chip>
            <Chip tone="red" active={attnSeverity === 'red'} onClick={() => setAttnSeverity('red')}>Urgent {attention.filter(a => a.severity === 'red').length}</Chip>
            <Chip tone="amber" active={attnSeverity === 'amber'} onClick={() => setAttnSeverity('amber')}>Review {attention.filter(a => a.severity !== 'red').length}</Chip>
            <span className="dash-filter-sep" />
            <Chip active={attnArea === 'all'} onClick={() => setAttnArea('all')}>Everything</Chip>
            {attnAreas.map(a => (
              <Chip key={a} active={attnArea === a} onClick={() => setAttnArea(a)}>{a} {attention.filter(x => areaGroup(x) === a).length}</Chip>
            ))}
          </div>
        )}
        {attention.length === 0 ? (
          <div className="dash-empty dash-empty-good"><i className="ti ti-circle-check" /> Nothing needs attention right now.</div>
        ) : attnShown.length === 0 ? (
          <div className="dash-empty dash-empty-good">Nothing matches these filters.</div>
        ) : (
          <div className="att-list">
            {attnShown.map((a, i) => (
              <Link key={i} to={a.link || '/dashboard'} className="att-item">
                <Dot rag={a.severity} />
                <div className="att-body">
                  <div className="att-title">{a.title}</div>
                  {a.detail && <div className="att-detail">{a.detail}</div>}
                </div>
                <span className="tag tag-muted att-area">{a.area}</span>
                <span className="recent-arrow">›</span>
              </Link>
            ))}
          </div>
        )}
      </Section>

      {/* ── Sales pipeline ── */}
      {sales && salesView && (
        <Section title="Sales pipeline" link="/sales/pipeline" linkLabel="Pipeline" innerRef={refs.sales}>
          <div className="sales-stats">
            <Link to="/sales/pipeline" className="sales-stat"><div className="sales-num">{sales.stats.open_count}</div><div className="stat-label">Open leads</div></Link>
            <Link to="/sales/pipeline" className="sales-stat"><div className="sales-num green">{money(sales.stats.open_value)}</div><div className="stat-label">Open value a year</div></Link>
            <Link to="/sales/pipeline" className="sales-stat"><div className={`sales-num ${salesView.hotCount ? 'red' : ''}`}>{salesView.hotCount}</div><div className="stat-label">Hot leads</div></Link>
            <Link to="/sales/pipeline" className="sales-stat"><div className={`sales-num ${sales.stats.overdue ? 'amber' : ''}`}>{sales.stats.overdue}</div><div className="stat-label">Overdue follow-ups{sales.stats.due_today ? ` · ${sales.stats.due_today} today` : ''}</div></Link>
            <Link to="/sales/pipeline" className="sales-stat"><div className="sales-num">{sales.stats.won}</div><div className="stat-label">Won{sales.stats.win_rate != null ? ` · ${sales.stats.win_rate}% win rate` : ''}</div></Link>
            {sales.stats.new_from_saved > 0 && (
              <Link to="/sales/search" className="sales-stat"><div className="sales-num accent">{sales.stats.new_from_saved}</div><div className="stat-label">New from saved searches</div></Link>
            )}
          </div>

          {salesView.open.length > 0 && (
            <div className="stage-bar" title="Open leads by stage">
              {salesView.byStage.filter(s => s.count).map(s => (
                <Link key={s.stage} to="/sales/pipeline" className="stage-seg" style={{ flex: s.count, background: STAGE_COLOURS[s.stage] }}>
                  <span>{s.stage} {s.count}</span>
                </Link>
              ))}
            </div>
          )}

          <div className="sales-lists">
            <div className="sales-col">
              <div className="cov-head">Hottest leads</div>
              {salesView.hot.length === 0 ? <div className="cov-none">No scored leads yet. Enrich leads in the Pipeline to score them.</div>
                : salesView.hot.map(l => (
                  <Link key={l.id} to={`/sales/pipeline?lead=${l.id}`} className="cov-item">
                    <span className="sales-name">{l.company_name}</span>
                    <span className="cov-meta">
                      <span className="sales-stage" style={{ color: STAGE_COLOURS[l.stage] }}>{l.stage}</span>
                      <span className={`sales-score band-${(l.score_band || '').toLowerCase()}`}>{l.score}</span>
                    </span>
                  </Link>
                ))}
            </div>
            <div className="sales-col">
              <div className="cov-head">Follow-ups (next 7 days)</div>
              {salesView.due.length === 0 ? <div className="cov-none">No follow-ups in the next 7 days. All caught up.</div>
                : salesView.due.map(l => (
                  <Link key={l.id} to={`/sales/pipeline?lead=${l.id}`} className="cov-item">
                    <span className="sales-name">
                      {l.company_name}
                      {l.next_action && <span className="sales-next"> · {l.next_action}</span>}
                    </span>
                    <span className={`cov-meta ${l.next_action_date <= salesView.today ? 'amber' : ''}`}>
                      {l.next_action_date < salesView.today ? `Overdue ${formatDate(l.next_action_date)}`
                        : l.next_action_date === salesView.today ? 'Today'
                        : formatDate(l.next_action_date)}
                    </span>
                  </Link>
                ))}
            </div>
          </div>
        </Section>
      )}

      {/* ── v1.7: client sites on an older tracking snippet ── */}
      {snippets && snippets.outdated && snippets.outdated.length > 0 && (
        <Section title="Tracking snippets to update" count={snippets.outdated.length}
          right={<span className="dash-showing">v{snippets.current} is current · last {snippets.days} days</span>}>
          <div className="att-list">
            {snippets.outdated.map(o => (
              <Link key={o.slug} to={`/clients/${o.slug}/domains?panel=snippet`} className="att-item">
                <Dot rag="amber" />
                <div className="att-body">
                  <div className="att-title">{o.name}{o.old_hosts && o.old_hosts.length ? ` · ${o.old_hosts.slice(0, 3).join(', ')}` : ''}</div>
                  <div className="att-detail">
                    Running v{(o.old_versions || []).join(', v')} on {o.old_hits} of {o.total_hits} page views
                    {o.partly_updated ? ' · new snippet already on some pages' : ''}
                    {o.old_last_seen ? ` · last seen ${formatDate(o.old_last_seen.slice(0, 10))}` : ''}
                  </div>
                </div>
                <span className="tag tag-muted att-area">Update snippet</span>
                <span className="recent-arrow">›</span>
              </Link>
            ))}
          </div>
        </Section>
      )}

      {/* ── Platform health and expiring, side by side ── */}
      <div className={`dash-grid ${health ? '' : 'dash-grid-single'}`}>
        {health && (
          <Section title="Platform health" link="/admin" linkLabel="Admin">
            <div className="health-list">
              {health.map(h => (
                <div key={h.label} className="health-row" title={(h.lines || []).join('\n')}>
                  <Dot rag={h.rag} />
                  <span className="health-label">{h.label.replace(' (24h)', ' (24 hours)')}</span>
                  <span className="health-value">{h.value}</span>
                </div>
              ))}
            </div>
          </Section>
        )}

        <Section title={`Expiring in the next ${data.expiry_days} days`} count={expiring.length || null} innerRef={refs.expiring}>
          {expiring.length > 0 && (
            <div className="dash-filters">
              <Chip active={expKind === 'all'} onClick={() => setExpKind('all')}>All</Chip>
              <Chip active={expKind === 'Domain'} onClick={() => setExpKind('Domain')}>Domains {expiring.filter(e => e.kind === 'Domain').length}</Chip>
              <Chip active={expKind === 'Certificate'} onClick={() => setExpKind('Certificate')}>Certificates {expiring.filter(e => e.kind === 'Certificate').length}</Chip>
              <span className="dash-filter-sep" />
              <Chip active={hideAutoRenew} onClick={() => setHideAutoRenew(v => !v)}>Hide auto-renewing</Chip>
            </div>
          )}
          {expShown.length === 0 ? (
            <div className="dash-empty dash-empty-good">{expiring.length ? 'Nothing matches these filters.' : 'No domains or certificates expiring.'}</div>
          ) : (
            <div className="exp-list">
              {expShown.map((e, i) => (
                <Link key={i} to={`/clients/${e.slug}/domains`} className="exp-item">
                  <span className={`exp-days ${e.days <= 7 ? 'red' : e.days <= 14 ? 'amber' : ''}`}>{expiryText(e.days)}</span>
                  <div className="att-body">
                    <div className="att-title">{e.name}</div>
                    <div className="att-detail">{e.client} · {e.kind}{e.date ? ` · ${formatDate(e.date)}` : ''}</div>
                  </div>
                  {e.auto_renew
                    ? <span className="tag tag-green" title={e.note}>Auto-renews</span>
                    : <span className="tag tag-muted">{e.kind}</span>}
                </Link>
              ))}
            </div>
          )}
        </Section>
      </div>

      {/* ── Client risk ── */}
      <Section title="Client risk" link="/manage" linkLabel="Client Management" innerRef={refs.risk}
        right={riskFiltered ? <span className="dash-showing">Showing {riskShown.length} of {riskClients.length}</span> : null}>
        <div className="dash-filters">
          <input className="dash-search" value={clientQuery} onChange={e => setClientQuery(e.target.value)} placeholder="Find a client…" />
          <Chip active={gradeBand === 'all'} onClick={() => setGradeBand('all')}>All grades</Chip>
          {GRADE_BANDS.map(b => (
            <Chip key={b.id} active={gradeBand === b.id} onClick={() => setGradeBand(gradeBand === b.id ? 'all' : b.id)}>
              {b.label} {riskClients.filter(c => b.test(c.grade ? String(c.grade)[0] : '')).length}
            </Chip>
          ))}
          <span className="dash-filter-sep" />
          <Chip active={issuesOnly} onClick={() => setIssuesOnly(v => !v)}>Has issues</Chip>
          <Chip tone="red" active={risingOnly} onClick={() => setRisingOnly(v => !v)}>Getting worse {riskClients.filter(c => c.delta > 0).length}</Chip>
          {riskFiltered && <button type="button" className="dash-clear" onClick={() => { resetClientFilters(); setActiveTile(''); }}>Clear filters</button>}
        </div>
        {riskClients.length === 0 ? (
          <div className="dash-empty">No clients yet.</div>
        ) : riskShown.length === 0 ? (
          <div className="dash-empty">No clients match these filters.</div>
        ) : (
          <>
            <div className="risk-table-wrap">
              <table className="risk-table stack-on-mobile">
                <thead>
                  <tr>
                    <th>Client</th>
                    <th>Grade</th>
                    <th className="num">Open issues</th>
                    <th className="num">Change</th>
                    <th>Biggest areas</th>
                    <th>Last assessed</th>
                  </tr>
                </thead>
                <tbody>
                  {riskVisible.map(c => (
                    <tr key={c.slug} onClick={() => navigate(`/clients/${c.slug}/cyber-security`)}>
                      <td className="risk-name wide">{c.name}</td>
                      <td data-label="Grade"><GradeBadge grade={c.grade} /></td>
                      <td data-label="Open issues" className="num mono">{c.issues}</td>
                      <td data-label="Change" className="num mono">
                        {c.delta == null ? <span className="muted">—</span>
                          : c.delta > 0 ? <span className="red">+{c.delta}</span>
                          : c.delta < 0 ? <span className="green">{c.delta}</span>
                          : <span className="muted">0</span>}
                      </td>
                      <td data-label="Biggest areas" className="risk-areas wide">
                        {c.top_issues.length === 0 ? <span className="muted">None</span>
                          : c.top_issues.map(t => `${t.label} (${t.count})`).join(', ')}
                      </td>
                      <td data-label="Last assessed" className="mono-date">
                        {c.last_assessment
                          ? <span className={c.assessment_age_days > data.stale_days ? 'amber' : ''}>{formatDate(c.last_assessment)}</span>
                          : <span className="muted">Never</span>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {!riskFiltered && riskShown.length > 10 && (
              <button className="dash-more" onClick={() => setShowAllClients(v => !v)}>
                {showAllClients ? 'Show top 10' : `Show all ${riskShown.length} clients`}
              </button>
            )}
          </>
        )}
      </Section>

      {/* ── Assessment coverage ── */}
      <Section title="Assessment coverage" innerRef={refs.coverage}>
        <div className="cov-grid">
          <div className="cov-col">
            <div className="cov-head">Never assessed <span className="dash-count">{coverage.never.length}</span></div>
            {coverage.never.length === 0 ? <div className="cov-none">Every client has been assessed.</div>
              : coverage.never.map(c => (
                <Link key={c.slug} to={`/clients/${c.slug}`} className="cov-item">{c.name}</Link>
              ))}
          </div>
          <div className="cov-col">
            <div className="cov-head">Older than {data.stale_days} days <span className="dash-count">{coverage.stale.length}</span></div>
            {coverage.stale.length === 0 ? <div className="cov-none">All assessments are up to date.</div>
              : coverage.stale.map(c => (
                <Link key={c.slug} to={`/clients/${c.slug}/reports`} className="cov-item">
                  <span>{c.name}</span>
                  <span className="cov-meta">{formatDate(c.date)}</span>
                </Link>
              ))}
          </div>
          <div className="cov-col">
            <div className="cov-head">Grade dropped <span className="dash-count">{coverage.dropped.length}</span></div>
            {coverage.dropped.length === 0 ? <div className="cov-none">No grade drops.</div>
              : coverage.dropped.map(c => (
                <Link key={c.slug} to={`/clients/${c.slug}/reports`} className="cov-item">
                  <span>{c.name}</span>
                  <span className="cov-meta"><GradeBadge grade={c.from} /> → <GradeBadge grade={c.to} /></span>
                </Link>
              ))}
          </div>
        </div>
      </Section>

      <div className="dash-quick">
        <Link to="/new" className="quick-card">
          <div className="quick-icon">+</div>
          <div>
            <div className="quick-title">Reports and Assessments</div>
            <div className="quick-desc">IT and Cyber Security Report, Website Analytics Report or External Cyber Security Assessment</div>
          </div>
        </Link>
      </div>
    </div>
  );
}
